import { useState, useCallback, useRef, useEffect } from 'react';

const STEP_ORDER = [
  'question', 'description', 'outcomes', 'visibility',
  'liquidity', 'resolution', 'preview',
];

const NEXT_STEP_AI_MESSAGES = {
  question: {
    content: "Great! Now let's add initial liquidity to enable trading:",
    component: 'liquidity',
    nextStep: 'liquidity',
  },
  description: {
    content: "Great! Now let's add initial liquidity to enable trading:",
    component: 'liquidity',
    nextStep: 'liquidity',
  },
};

const STEP_TO_COMPONENT = {
  description: 'description',
  outcomes: 'outcomes',
  visibility: 'visibility',
  resolution: 'resolution',
  resolver: 'resolver',
  liquidity: 'liquidity',
};

const STEP_TO_MESSAGE = {
  outcomes: 'Edit outcomes and probabilities:',
  visibility: 'Who should be able to see and trade on this market?',
  resolution: 'Set your market timeline:',
  resolver: 'Who should resolve this market when the time comes?',
  description: 'Edit the market description:',
  liquidity: 'Adjust initial liquidity:',
};

export const useChatManager = ({
  currentStep,
  setCurrentStep,
  isMobile,
  walletConnected,
  reset: resetMarket,
}) => {
  const [chatHistory, setChatHistory] = useState([]);
  const [showWelcome, setShowWelcome] = useState(true);
  const [stepHistory, setStepHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('chat');
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const chatEndRef = useRef(null);
  const mainRef = useRef(null);

  // ─── Auto-scroll on chat changes ────────────────────
  useEffect(() => {
    const doScroll = () => {
      const scrollContainer =
        chatEndRef.current && chatEndRef.current.closest('[data-chat-scroll]');
      if (scrollContainer) scrollContainer.scrollTop = scrollContainer.scrollHeight;
    };
    doScroll();
    const timers = [50, 150, 300, 500, 800, 1200].map((ms) =>
      setTimeout(() => {
        const scrollContainer =
          chatEndRef.current && chatEndRef.current.closest('[data-chat-scroll]');
        if (scrollContainer)
          scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'smooth' });
      }, ms)
    );
    return () => timers.forEach(clearTimeout);
  }, [chatHistory, currentStep]);

  // ─── Track step history ─────────────────────────────
  useEffect(() => {
    if (currentStep !== 'question' && currentStep !== 'success') {
      setStepHistory((prev) => {
        if (prev[prev.length - 1] !== currentStep) return [...prev, currentStep];
        return prev;
      });
    }
  }, [currentStep]);

  // ─── Auto-close wizard only when first entering preview ──
  const prevStepRef = useRef(currentStep);
  useEffect(() => {
    const prevStep = prevStepRef.current;
    prevStepRef.current = currentStep;

    if (currentStep === 'preview' && prevStep !== 'preview') {
      // Entering preview — auto-close the wizard
      if (isMobile) setActiveTab('preview');
      else setChatCollapsed(true);
    } else if (currentStep !== 'preview') {
      // Leaving preview — ensure wizard is open
      setChatCollapsed(false);
    }
  }, [currentStep, isMobile]);

  // ─── Helpers ────────────────────────────────────────
  const getLiquidityStepMessage = useCallback(() => {
    if (!walletConnected) {
      return {
        type: 'ai',
        content: "Great! First, let's connect your wallet \u{1F517}",
        component: 'wallet-connect',
      };
    }
    return {
      type: 'ai',
      content: "Great! Now let's add initial liquidity to enable trading:",
      component: 'liquidity',
    };
  }, [walletConnected]);

  const addMessages = useCallback((...messages) => {
    setChatHistory((prev) => [...prev, ...messages]);
  }, []);

  const replaceHistory = useCallback((newHistory) => {
    setChatHistory(newHistory);
  }, []);

  // ─── Category selection ─────────────────────────────
  const handleCategorySelect = useCallback((category) => {
    setSelectedCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
    );
  }, []);

  // ─── Navigate to a step (for editing) ───────────────
  const navigateToStep = useCallback(
    (step) => {
      if (step === 'question') {
        setSavedSnapshot({ chatHistory: [...chatHistory], lastStep: currentStep });
        const withoutPicker = chatHistory.filter((m) => m.component !== 'edit-picker');
        setChatHistory([
          ...withoutPicker,
          { type: 'ai', content: 'Edit your question:', component: 'question-edit' },
        ]);
        setCurrentStep('question');
        setActiveTab('chat');
        setChatCollapsed(false);
        return;
      }
      if (step === 'cancel-question-edit') {
        if (savedSnapshot) {
          setChatHistory(savedSnapshot.chatHistory);
          setCurrentStep(savedSnapshot.lastStep);
          setSavedSnapshot(null);
          if (savedSnapshot.lastStep === 'preview') {
            if (isMobile) setActiveTab('preview');
            else setChatCollapsed(true);
          }
        }
        return;
      }
      const component = STEP_TO_COMPONENT[step];
      if (component) {
        setSavedSnapshot({ chatHistory: [...chatHistory], lastStep: currentStep });
        if (currentStep === 'preview') {
          const withoutPicker = chatHistory.filter((m) => m.component !== 'edit-picker');
          setChatHistory([
            ...withoutPicker,
            { type: 'ai', content: STEP_TO_MESSAGE[step] || `Edit ${step}:`, component },
          ]);
        } else {
          const idx = chatHistory.findIndex((m) => m.component === component);
          if (idx >= 0) {
            setChatHistory(chatHistory.slice(0, idx + 1));
          } else {
            setChatHistory((prev) => [
              ...prev,
              { type: 'ai', content: STEP_TO_MESSAGE[step] || `Edit ${step}:`, component },
            ]);
          }
        }
        setCurrentStep(step);
        setActiveTab('chat');
        setChatCollapsed(false);
      }
    },
    [chatHistory, currentStep, savedSnapshot, setCurrentStep, isMobile]
  );

  // ─── Back navigation ────────────────────────────────
  const handleBack = useCallback(() => {
    const currentIndex = STEP_ORDER.indexOf(currentStep);
    if (currentIndex > 0) {
      const prevStep = STEP_ORDER[currentIndex - 1];
      if (prevStep === 'question') {
        setShowWelcome(true);
        setChatHistory([]);
        resetMarket();
        setSavedSnapshot(null);
      } else {
        navigateToStep(prevStep);
      }
    } else {
      setShowWelcome(true);
      setChatHistory([]);
      resetMarket();
      setSavedSnapshot(null);
    }
  }, [currentStep, resetMarket, navigateToStep]);

  // ─── Restore/discard snapshot ───────────────────────
  const handleRestoreKeep = useCallback(() => {
    if (!savedSnapshot) return;
    const editedSummary = [...chatHistory]
      .reverse()
      .find((m) => m.type === 'user' && m.targetStep);
    const restoredHistory = savedSnapshot.chatHistory.map((m) => {
      if (editedSummary && m.type === 'user' && m.targetStep === editedSummary.targetStep)
        return editedSummary;
      return m;
    });
    setChatHistory(restoredHistory);
    setCurrentStep(savedSnapshot.lastStep);
    setSavedSnapshot(null);
    if (savedSnapshot.lastStep === 'preview') setActiveTab('preview');
  }, [savedSnapshot, chatHistory, setCurrentStep]);

  const handleRestoreReset = useCallback(() => {
    if (!savedSnapshot) return;
    const editedSummary = [...chatHistory]
      .reverse()
      .find((m) => m.type === 'user' && m.targetStep);
    const editedStep = editedSummary?.targetStep;
    const withoutRestore = chatHistory.filter((m) => m.component !== 'restore');
    const nextInfo = editedStep ? NEXT_STEP_AI_MESSAGES[editedStep] : null;
    if (nextInfo) {
      const msg =
        nextInfo.nextStep === 'liquidity'
          ? getLiquidityStepMessage()
          : { type: 'ai', content: nextInfo.content, component: nextInfo.component };
      setChatHistory([...withoutRestore, msg]);
      setCurrentStep(nextInfo.nextStep);
    }
    setSavedSnapshot(null);
  }, [savedSnapshot, chatHistory, setCurrentStep, getLiquidityStepMessage]);

  // ─── Full reset ─────────────────────────────────────
  const handleReset = useCallback(() => {
    resetMarket();
    setSelectedCategories([]);
    setShowWelcome(true);
    setChatHistory([]);
    setStepHistory([]);
    setActiveTab('chat');
    setSavedSnapshot(null);
  }, [resetMarket]);

  // ─── Tab switching helper ───────────────────────────
  const handleTabChange = useCallback(
    (tab) => {
      if (tab === 'chat' && currentStep === 'preview') {
        const hasEditPicker = chatHistory.some((m) => m.component === 'edit-picker');
        if (!hasEditPicker) {
          setChatHistory((prev) => [
            ...prev,
            { type: 'ai', content: '\u270f\ufe0f Which part would you like to edit?', component: 'edit-picker' },
          ]);
        }
      }
      setActiveTab(tab);
    },
    [currentStep, chatHistory]
  );

  // ─── Open chat from collapsed ───────────────────────
  const handleOpenChat = useCallback(() => {
    setChatCollapsed(false);
    if (currentStep === 'preview') {
      const hasEditPicker = chatHistory.some((m) => m.component === 'edit-picker');
      if (!hasEditPicker) {
        setChatHistory((prev) => [
          ...prev,
          { type: 'ai', content: '\u270f\ufe0f Which part would you like to edit?', component: 'edit-picker' },
        ]);
      }
    }
  }, [currentStep, chatHistory]);

  // ─── Close chat sidebar ─────────────────────────────
  const handleCloseChat = useCallback(() => {
    if (savedSnapshot && savedSnapshot.lastStep === 'preview') {
      setChatHistory(savedSnapshot.chatHistory);
      setCurrentStep('preview');
      setSavedSnapshot(null);
    }
    setChatCollapsed(true);
  }, [savedSnapshot, setCurrentStep]);

  // ─── Computed values ────────────────────────────────
  const canGoBack = currentStep !== 'question' && currentStep !== 'success';

  return {
    // Refs
    chatEndRef,
    mainRef,

    // State
    chatHistory,
    setChatHistory,
    showWelcome,
    setShowWelcome,
    stepHistory,
    activeTab,
    setActiveTab,
    chatCollapsed,
    setChatCollapsed,
    savedSnapshot,
    setSavedSnapshot,
    selectedCategories,
    canGoBack,

    // Actions
    addMessages,
    replaceHistory,
    getLiquidityStepMessage,
    handleCategorySelect,
    navigateToStep,
    handleBack,
    handleRestoreKeep,
    handleRestoreReset,
    handleReset,
    handleTabChange,
    handleOpenChat,
    handleCloseChat,
  };
};
